"use client";

import type { TournamentDetailView } from "@clutch/auth/actions/tournaments";
import { Copy01Icon, CopyCheckIcon, Download01Icon, Loading03Icon, PrinterIcon, Share08Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TournamentCard } from "./tournament-card";
type ShareTournamentDialogProps = {
  tournament: TournamentDetailView;
  trigger: React.ReactNode;
};
export function ShareTournamentDialog({
  tournament,
  trigger
}: ShareTournamentDialogProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const url = useMemo(() => {
    if (typeof window !== "undefined") {
      return `${window.location.origin}/t/${tournament.slug}`;
    }
    return `/t/${tournament.slug}`;
  }, [tournament.slug]);
  const cardImageUrl = `/api/tournament-card/${encodeURIComponent(tournament.slug)}`;
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(id);
  }, [copied]);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };
  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const response = await fetch(cardImageUrl);
      if (!response.ok) throw new Error("Failed to generate card");
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `clutch-tournament-${tournament.slug}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      toast.success("Card downloaded");
    } catch {
      toast.error("Could not download card");
    } finally {
      setIsDownloading(false);
    }
  };
  const handlePrint = async () => {
    if (isPrinting) return;
    setIsPrinting(true);
    try {
      const printWindow = window.open("", "_blank", "width=900,height=1100");
      if (!printWindow) {
        toast.error("Allow pop-ups to print");
        return;
      }
      printWindow.document.write(`
<!doctype html>
<html>
<head>
<title>${tournament.name} — Clutch</title>
<style>
	@page { size: auto; margin: 16mm; }
	* { box-sizing: border-box; }
	body { margin: 0; display: flex; align-items: center; justify-content: center; min-height: 100vh; background: #f3f4f6; font-family: system-ui, sans-serif; }
	img { max-width: 100%; height: auto; display: block; }
	.wrap { padding: 24px; }
</style>
</head>
<body>
	<div class="wrap">
		<img src="${cardImageUrl}" alt="${tournament.name.replace(/"/g, "&quot;")}" onload="setTimeout(() => { window.focus(); window.print(); }, 250)" />
	</div>
</body>
</html>
`);
      printWindow.document.close();
    } catch {
      toast.error("Could not open print view");
    } finally {
      setIsPrinting(false);
    }
  };
  const handleNativeShare = async () => {
    if (typeof navigator === "undefined" || !navigator.share) {
      await handleCopy();
      return;
    }
    try {
      await navigator.share({
        title: tournament.name,
        text: `${tournament.name} — ${tournament.sportName}`,
        url
      });
    } catch (err) {
      if ((err as Error)?.name !== "AbortError") {
        toast.error("Could not share");
      }
    }
  };
  const supportsNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  return <Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>{trigger}</DialogTrigger>
			<DialogContent className="max-h-[90svh] gap-4 overflow-y-auto p-4 sm:max-w-xl sm:gap-6 sm:p-6">
				<DialogHeader className="gap-0 pr-8">
					<DialogTitle className="font-semibold text-lg sm:text-xl">
						Share tournament
					</DialogTitle>
					<DialogDescription>
						Download the card to share on socials, print as a flyer, or copy the
						link.
					</DialogDescription>
				</DialogHeader>

				<div className="rounded-2xl border border-border/70 bg-muted/30 p-3 sm:p-4">
					<TournamentCard tournament={tournament} url={url} />
				</div>

				<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
					<Button onClick={handleDownload} disabled={isDownloading} size="lg" className="w-full">
						<HugeiconsIcon icon={isDownloading ? Loading03Icon : Download01Icon} className={isDownloading ? "mr-2 h-4 w-4 animate-spin" : "mr-2 h-4 w-4"} />
						{isDownloading ? "Generating…" : "Download card"}
					</Button>
					<Button onClick={handlePrint} disabled={isPrinting} size="lg" variant="outline" className="w-full">
						<HugeiconsIcon icon={PrinterIcon} className="mr-2 h-4 w-4" />
						Print
					</Button>
					<Button onClick={handleCopy} size="lg" variant="outline" className="w-full">
						<HugeiconsIcon icon={copied ? CopyCheckIcon : Copy01Icon} className="mr-2 h-4 w-4" />
						{copied ? "Copied" : "Copy link"}
					</Button>
					{supportsNativeShare && <Button onClick={handleNativeShare} size="lg" variant="outline" className="w-full">
							<HugeiconsIcon icon={Share08Icon} className="mr-2 h-4 w-4" />
							Share…
						</Button>}
				</div>
			</DialogContent>
		</Dialog>;
}